import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import { switchMap } from 'rxjs/operators';
import {
    ManagePdfReportCreateRequest,
    ManagePdfReportDetail,
    ManagePdfReportMutationResponse,
    ManagePdfReportProjectIdsRequest,
    ManagePdfReportReorderProjectsRequest,
    ManagePdfReportSummary,
    ManagePdfReportUpdatePropertiesRequest,
    ManagePdfReportUpdateRequest
} from './manage-pdf-reports.types';
import { ProjectSummarySort } from 'src/app/project-summary/data-access/project-summary.types';

@Injectable({ providedIn: 'root' })
export class ManagePdfReportsDataService {
    constructor(private http: HttpClient) { }

    getReports(sort?: ProjectSummarySort): Observable<ManagePdfReportSummary[]> {
        let query = '';
        if (sort?.sortField) {
            query = `?sortField=${encodeURIComponent(sort.sortField)}&sortDesc=${sort.sortDesc ? 'true' : 'false'}`;
        }

        return this.http.get<ManagePdfReportSummary[]>(`/api/PdfProjectReport/getreports${query}`);
    }

    getReport(guid: string, sort?: ProjectSummarySort): Observable<ManagePdfReportDetail> {
        let query = '';
        if (sort?.sortField) {
            query = `?sortField=${encodeURIComponent(sort.sortField)}&sortDesc=${sort.sortDesc ? 'true' : 'false'}`;
        }

        return this.http.get<ManagePdfReportDetail>(`/api/pdfreport/${guid}${query}`);
    }

    pollReports(pollIntervalMs: number, sort?: ProjectSummarySort): Observable<ManagePdfReportSummary[]> {
        return timer(0, pollIntervalMs).pipe(
            switchMap(() => this.getReports(sort))
        );
    }

    pollReport(guid: string, pollIntervalMs: number, sort?: ProjectSummarySort): Observable<ManagePdfReportDetail> {
        return timer(0, pollIntervalMs).pipe(
            switchMap(() => this.getReport(guid, sort))
        );
    }

    getReportProjectSummaries(reportGuid: string, sort: ProjectSummarySort, limit: number): Observable<unknown[]> {
        const paging = {
            limit: Math.max(1, Number(limit) || 1),
            offset: 0
        };

        const sorting = {
            sort_order: sort?.sortField || 'ProjectName',
            sort_desc: !!sort?.sortDesc
        };

        return this.http.get<any[]>(`/api/projectsummary/${reportGuid}`, {
            params: {
                paging: JSON.stringify(paging),
                sorting: JSON.stringify(sorting),
                source: 'pdfreport'
            }
        }).pipe(
            map((result) => Array.isArray(result) && result.length > 0
                ? (result[0]?.summaries || [])
                : [])
        );
    }

    runReport(reportGuid: string): Observable<void> {
        return this.http.put<void>(`/api/pdfprojectreport/runreport/${reportGuid}`, {
            reportId: reportGuid,
            requestDate: new Date()
        });
    }

    cancelReport(reportGuid: string): Observable<void> {
        return this.http.put<void>(`/api/pdfprojectreport/cancel/${reportGuid}`, {});
    }

    deleteReport(reportGuid: string): Observable<void> {
        return this.http.delete<void>(`/api/pdfprojectreport/delete/${reportGuid}`);
    }

    createReport(payload: ManagePdfReportCreateRequest): Observable<ManagePdfReportMutationResponse> {
        return this.http.post<ManagePdfReportMutationResponse>('/api/pdfprojectreport/create', payload);
    }

    updateReport(reportGuid: string, payload: ManagePdfReportUpdateRequest): Observable<void> {
        return this.http.put<void>(`/api/pdfprojectreport/update/${reportGuid}`, {
            reportId: payload.reportGuid,
            title: payload.title,
            description: payload.description,
            fileName: payload.fileName
        });
    }

    updateProperties(guid: string, payload: ManagePdfReportUpdatePropertiesRequest): Observable<void> {
        return this.http.put<void>(`/api/pdfreport/updateproperties/${guid}`, {
            reportId: payload.reportGuid,
            title: payload.title,
            description: payload.description,
            fileName: payload.fileName
        });
    }

    reorderProjects(guid: string, payload: ManagePdfReportReorderProjectsRequest): Observable<void> {
        return this.http.put<void>(`/api/pdfreport/reorderprojects/${guid}`, payload);
    }

    removeProjects(guid: string, payload: ManagePdfReportProjectIdsRequest): Observable<void> {
        return this.http.patch<void>(`/api/pdfreport/${guid}?operation=removeProjects`, payload);
    }

    restoreProject(guid: string, payload: ManagePdfReportProjectIdsRequest): Observable<void> {
        return this.http.patch<void>(`/api/pdfreport/${guid}?operation=restoreProject`, payload);
    }

    addProjects(guid: string, payload: ManagePdfReportProjectIdsRequest): Observable<void> {
        return this.http.patch<void>(`/api/pdfreport/${guid}?operation=addProjects`, payload);
    }

    downloadUrl(reportGuid: string): string {
        return `/api/reports/pdfprojects/${reportGuid}`;
    }
}
